/** The roadmap: every project laid across months, coloured by health.
 *
 * Built for a planning session projected on a wall, which drives two choices: the
 * current month is marked so the eye finds "now" immediately, and undated projects are
 * listed underneath rather than dropped. A project with no dates is a planning gap, and
 * hiding it is how work goes missing.
 */

import { useMemo, useState } from 'react'

import { EmptyState, ErrorState, Loading, Pill, SectionHeader } from '@/components/ui'
import { ragTone, stageLabel, teamLabel } from '@/lib/domain'
import {
  ProjectDrillDown,
  type DrillDownProject,
} from '@/features/reports/ProjectDrillDown'
import { useRoadmap, type RoadmapProject } from '@/features/reports/useReports'

const RAG_COLOR: Record<string, string> = {
  green: 'var(--status-green)',
  amber: 'var(--status-amber)',
  red: 'var(--status-red)',
  none: 'var(--status-info)',
}

// What each colour MEANS. A legend that reads "green = green" tells nobody anything.
const RAG_MEANING: Record<string, string> = {
  green: 'On track',
  amber: 'At risk',
  red: 'Behind',
  none: 'No health set',
}

type Grouping = 'workstream' | 'owner' | 'none'

export function RoadmapPage() {
  const [months, setMonths] = useState(12)
  const [grouping, setGrouping] = useState<Grouping>('workstream')
  // The undated list carries the same fields minus the bar indexes, so the modal's own
  // narrower shape is what both row kinds satisfy.
  const [selected, setSelected] = useState<DrillDownProject | null>(null)
  const { data, isPending, error } = useRoadmap(months)

  const periods = data?.periods ?? []
  // Memoised: `?? []` builds a new array each render, defeating the grouping memo below.
  const projects = useMemo(() => data?.projects ?? [], [data])

  const groups = useMemo(() => {
    if (grouping === 'none') return [{ label: 'All projects', projects }]
    const byKey = new Map<string, RoadmapProject[]>()
    for (const project of projects) {
      const key =
        grouping === 'workstream' ? project.team || 'unassigned' : project.ownerName || 'Unassigned'
      byKey.set(key, [...(byKey.get(key) ?? []), project])
    }
    return [...byKey.entries()]
      .map(([key, items]) => ({
        label: grouping === 'workstream' ? teamLabel(key) : key,
        projects: items,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [projects, grouping])

  if (isPending) return <Loading label="Building the roadmap" />
  if (error) return <ErrorState error={error} />

  return (
    <div className="mx-auto max-w-[1600px] pt-2">
      <header className="mb-6">
        <h1 className="font-heading text-3xl text-fg">Roadmap</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Every project across time, coloured by health. Starts two months back, so anything
          that has slipped is still on the wall.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(['workstream', 'owner', 'none'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setGrouping(option)}
              className="px-3 py-1.5 text-sm font-semibold"
              style={
                grouping === option
                  ? { backgroundColor: 'var(--sidebar-bg)', color: 'var(--sidebar-fg)' }
                  : { color: 'var(--fg-muted)' }
              }
            >
              {option === 'workstream' ? 'By team' : option === 'owner' ? 'By owner' : 'Flat'}
            </button>
          ))}
        </div>
        <select
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg"
          value={months}
          onChange={(event) => setMonths(Number(event.target.value))}
          aria-label="Horizon"
        >
          {[6, 12, 18, 24].map((value) => (
            <option key={value} value={value}>
              {value} months
            </option>
          ))}
        </select>
      </div>

      {projects.length === 0 ? (
        <EmptyState title="No dated projects in this window">
          Give a project a start or target date and it will appear here.
        </EmptyState>
      ) : (
        <div className="card overflow-x-auto">
          <div className="min-w-[900px] p-4">
            {/* Column headers */}
            <div
              className="mb-2 grid gap-px"
              style={{ gridTemplateColumns: `220px repeat(${periods.length}, minmax(52px, 1fr))` }}
            >
              <div />
              {periods.map((period) => (
                <div
                  key={period.key}
                  className="micro-label px-1 text-center text-fg-muted"
                  style={
                    period.isCurrent
                      ? {
                          color: 'var(--brand-on)',
                          backgroundColor: 'color-mix(in srgb, var(--brand) 25%, transparent)',
                          borderRadius: 4,
                        }
                      : undefined
                  }
                >
                  {period.isCurrent ? 'NOW' : period.label.split(' ')[0]}
                </div>
              ))}
            </div>

            {groups.map((group) => (
              <div key={group.label} className="mb-4">
                <h3 className="section-rule mb-2 text-fg">{group.label}</h3>
                <div className="space-y-1">
                  {group.projects.map((project) => (
                    <div
                      key={project.id}
                      className="grid items-center gap-px"
                      style={{
                        gridTemplateColumns: `220px repeat(${periods.length}, minmax(52px, 1fr))`,
                      }}
                    >
                      {/* The row is a question ("why is this here?") and the modal is
                          the answer, so the name is a button rather than a label. */}
                      <button
                        type="button"
                        className="truncate pr-3 text-left text-sm text-fg underline-offset-2 hover:underline"
                        title={`${project.name} — open sub tasks and resources`}
                        onClick={() => setSelected(project)}
                      >
                        {project.name}
                      </button>
                      {periods.map((period, index) => {
                        const inBar = index >= project.startIndex && index <= project.endIndex
                        const isStart = index === project.startIndex
                        const isEnd = index === project.endIndex
                        return (
                          <div key={period.key} className="h-6 px-px">
                            {inBar ? (
                              <div
                                className="flex h-full cursor-pointer items-center"
                                onClick={() => setSelected(project)}
                                style={{
                                  backgroundColor: RAG_COLOR[project.ragStatus] ?? RAG_COLOR.none,
                                  borderTopLeftRadius: isStart ? 999 : 0,
                                  borderBottomLeftRadius: isStart ? 999 : 0,
                                  borderTopRightRadius: isEnd ? 999 : 0,
                                  borderBottomRightRadius: isEnd ? 999 : 0,
                                }}
                                title={`${project.name} — ${stageLabel(project.stage)}, ${project.percentComplete}%`}
                              >
                                {isStart ? (
                                  <span className="pl-2 text-[10px] font-semibold text-white">
                                    {project.percentComplete}%
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <div
                                className="h-full"
                                style={
                                  period.isCurrent
                                    ? {
                                        backgroundColor:
                                          'color-mix(in srgb, var(--brand) 8%, transparent)',
                                      }
                                    : undefined
                                }
                              />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-4 flex flex-wrap gap-3 border-t border-border pt-3">
              {(['green', 'amber', 'red', 'none'] as const).map((rag) => (
                <span key={rag} className="flex items-center gap-2 text-xs text-fg-muted">
                  <span
                    className="inline-block h-3 w-6 rounded-full"
                    style={{ backgroundColor: RAG_COLOR[rag] }}
                  />
                  {RAG_MEANING[rag]}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {(data?.undated ?? []).length > 0 ? (
        <div className="mt-8">
          <SectionHeader>Not on the roadmap</SectionHeader>
          <p className="mb-2 text-xs text-fg-muted">
            No start or target date, so there is nowhere honest to draw them. Listed here rather
            than hidden.
          </p>
          <ul className="card divide-y divide-border">
            {(data?.undated ?? []).map((project) => (
              <li key={project.id} className="flex items-center justify-between px-4 py-2">
                <button
                  type="button"
                  className="text-left text-sm text-fg underline-offset-2 hover:underline"
                  onClick={() => setSelected(project)}
                >
                  {project.name}
                </button>
                <div className="flex items-center gap-2">
                  {project.ragStatus !== 'none' ? (
                    <Pill tone={ragTone(project.ragStatus)}>{project.ragStatus}</Pill>
                  ) : null}
                  <span className="text-xs text-fg-muted">
                    {project.teamShortLabel}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {selected ? <ProjectDrillDown project={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  )
}
